import { useCallback, useState } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { InfiniteScrollSentinel } from '@/components/data-table/infinite-scroll-sentinel';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { CLIENT_CATEGORISATION_COLUMNS, openUserInNewTab } from '@/features/client-categorisation/client-categorisation-columns';
import { ClientCategorisationExportButton } from '@/features/client-categorisation/client-categorisation-export-button';
import { ClientCategorisationFilterBar } from '@/features/client-categorisation/client-categorisation-filter-bar';
import { useClientCategorisation } from '@/hooks/queries/users/use-client-categorisation';
import type { ClientCategorisation, UserClientCategorisation } from '@/services/api/client-categorisation';

// Stable fallback — see data-table.tsx EMPTY_DATA rationale
const NO_ROWS: UserClientCategorisation[] = [];

const getRowPid = (row: UserClientCategorisation) => row.pid;

// Row click → same legacy new-tab user link as the PID cell's button
const handleRowClick = (row: UserClientCategorisation) => openUserInNewTab(row.pid);

const ClientCategorisationPage = () => {
  const [categorisation, setCategorisation] = useState<ClientCategorisation | undefined>(undefined);
  const query = useClientCategorisation(categorisation);
  const rows = query.data?.rows ?? NO_ROWS;

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isError: isQueryError } = query;
  // Guarded like trading-panel (review follow-up); TanStack dedupes in-flight fetches anyway
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isQueryError) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isQueryError]);

  // Non-latching error: Retry resumes the LEK chain when pages are
  // already loaded, otherwise restarts from the first page.
  const handleRetry = () => {
    if (query.data && query.hasNextPage) {
      void query.fetchNextPage();
      return;
    }
    void query.refetch();
  };

  const showSentinel = !query.isError && !query.isPending && (query.hasNextPage || query.isFetchingNextPage);
  const showEndCaption = !query.isError && !query.isPending && !showSentinel && rows.length > 0;

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title="Client Categorisation" />

      {/* Filter chips left; export button right (legacy header-aside slot) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ClientCategorisationFilterBar onChange={setCategorisation} value={categorisation} />
        <ClientCategorisationExportButton categorisation={categorisation} />
      </div>

      {query.isError ? <QueryErrorBanner message="Please try again later." onRetry={handleRetry} title="Error loading client categorisation" /> : null}

      {/* Table stays mounted through refetches and next-page errors — already
          loaded rows remain visible under the alert above */}
      <DataTable columns={CLIENT_CATEGORISATION_COLUMNS} data={rows} emptyMessage="No results" getRowId={getRowPid} isLoading={query.isPending} onRowClick={handleRowClick} />

      {/* isLoading covers refetches too so the observer never fires mid-fetch;
          it also doubles as the next-page loading panel */}
      {showSentinel ? <InfiniteScrollSentinel hasMore={query.hasNextPage} isLoading={query.isFetchingNextPage} onLoadMore={handleLoadMore} /> : null}
      {/* The shared sentinel's end copy is generic — legacy caption preserved here */}
      {showEndCaption ? <p className="py-4 text-center text-xs text-muted-foreground">No more client categorisation</p> : null}
    </div>
  );
};
ClientCategorisationPage.displayName = 'ClientCategorisationPage';

export default ClientCategorisationPage;
